import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search, ChevronRight, Users, X,
  UserPlus, Zap, AlertCircle, ChevronDown, Tag, Star, Ban, Clock, Download
} from 'lucide-react'
import { contactsApi, Contact, ContactsFilters } from '@/services/contactsApi'
import { tagsApi } from '@/services/tagsApi'
import { ContactPanel } from '@/components/chat/ContactPanel'
import {
  validatePhoneNumber,
  validateEmailAddress,
} from './contactValidators'
import { downloadContactsCsv } from './contactCsvExport'

const TABS = ['Todos', 'Recientes', 'Favoritos'] as const
type Tab = (typeof TABS)[number]

const CHANNELS = [
  { value: '', label: 'Todos los canales' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'webchat', label: 'WebChat' },
  { value: 'manual', label: 'Manual' },
]

const STATUSES = [
  { value: '', label: 'Todos los estados' },
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'blocked', label: 'Bloqueado' },
]

function initials(contact: Contact): string {
  const name = contact.name || contact.waContact || '?'
  const parts = name.trim().split(' ')
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const AVATAR_COLORS = [
  'bg-[#075f55]/20 text-[#075f55]',
  'bg-blue-100 text-blue-600',
  'bg-amber-100 text-amber-600',
  'bg-purple-100 text-purple-600',
  'bg-rose-100 text-rose-600',
  'bg-teal-100 text-teal-600',
]

function avatarColor(id: string): string {
  const idx = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length
  return AVATAR_COLORS[idx]
}

function formatLastContact(date: Date | null): string {
  if (!date) return 'Sin actividad'
  const d = new Date(date)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Ahora mismo'
  if (diffMin < 60) return `Hace ${diffMin} min`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `Hace ${diffH}h`
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
}

function isRecentlyActive(date: Date | null): boolean {
  if (!date) return false
  return new Date().getTime() - new Date(date).getTime() < 24 * 60 * 60 * 1000
}

// ────────── Dropdown chip ──────────
interface FilterChipProps {
  icon: React.ReactNode
  label: string
  active: boolean
  children: React.ReactNode
}

function FilterChip({ icon, label, active, children }: FilterChipProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-medium transition-colors ${
          active
            ? 'bg-[#075f55] text-white'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        }`}
      >
        {icon}
        <span>{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-30 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[160px]"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  )
}

// ────────── New Contact Modal ──────────
interface NewContactModalProps {
  onClose: () => void
  onCreated: () => void
}

function NewContactModal({ onClose, onCreated }: NewContactModalProps) {
  const [form, setForm] = useState({
    waContact: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    country: '',
    notes: '',
    source: 'whatsapp',
  })
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [phoneError, setPhoneError] = useState('')
  const [emailError, setEmailError] = useState('')

  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: () => {
      const { role, country, ...rest } = form
      const phoneValidation = validatePhoneNumber(form.waContact)
      return contactsApi.createContact({
        ...rest,
        waContact: phoneValidation.normalized || form.waContact.trim(),
        position: role || undefined,          // F1-T9: map 'role' → 'position'
        customFields: country ? { country } : undefined, // F1-T9: store country in customFields
        source: form.source || 'manual',
        tagIds: selectedTagIds,
      } as any)
    },
    onSuccess: () => { onCreated(); onClose() },
  })

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm((f) => ({ ...f, [k]: e.target.value }))
      if (k === 'email') setEmailError('')
      if (k === 'waContact') setPhoneError('')
    }

  const validateEmail = () => {
    const res = validateEmailAddress(form.email)
    if (!res.isValid) {
      setEmailError(res.error || 'Ingresa un email válido')
    } else {
      setEmailError('')
    }
  }

  const validatePhone = () => {
    if (!form.waContact.trim()) return
    const res = validatePhoneNumber(form.waContact)
    if (!res.isValid) {
      setPhoneError(res.error || 'Número de WhatsApp inválido')
    } else {
      setPhoneError('')
      setForm((f) => ({ ...f, waContact: res.normalized }))
    }
  }

  const handleSubmit = () => {
    const phoneRes = validatePhoneNumber(form.waContact)
    if (!phoneRes.isValid) {
      setPhoneError(phoneRes.error || 'Número de WhatsApp inválido')
      return
    }
    const emailRes = validateEmailAddress(form.email)
    if (!emailRes.isValid) {
      setEmailError(emailRes.error || 'Ingresa un email válido')
      return
    }
    createMutation.mutate()
  }

  const toggleTag = (id: string) =>
    setSelectedTagIds((prev) => prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id])

  const fieldClass = (hasError?: boolean) =>
    `w-full h-12 rounded-lg border px-4 text-sm bg-white focus:outline-none focus:ring-1 transition-colors ${
      hasError
        ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
        : 'border-gray-200 focus:border-[#075f55] focus:ring-[#075f55]/20'
    }`

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md z-10 flex flex-col max-h-[90svh]">

        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="w-9 h-9 rounded-xl bg-[#075f55]/10 flex items-center justify-center flex-shrink-0">
            <UserPlus className="h-5 w-5 text-[#075f55]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-gray-900">Nuevo contacto</h2>
            <p className="text-xs text-gray-400">Completa la información del contacto</p>
          </div>
          <button onClick={onClose} className="btn-icon text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

          {/* Canal / ID */}
          <div>
            <label htmlFor="new-contact-wa" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Canal WhatsApp <span className="text-red-500">*</span>
            </label>
            <input
              id="new-contact-wa"
              className={fieldClass(!!phoneError)}
              placeholder="+5491123456789"
              value={form.waContact}
              onChange={set('waContact')}
              onBlur={validatePhone}
              aria-invalid={!!phoneError}
              aria-describedby={phoneError ? 'waContact-error' : undefined}
            />
            {phoneError && (
              <p id="waContact-error" className="flex items-center gap-1 mt-1 text-xs text-red-500">
                <AlertCircle className="h-3 w-3" />
                {phoneError}
              </p>
            )}
          </div>

          {/* Nombre completo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nombre completo</label>
            <input
              className={fieldClass()}
              placeholder="Ej: María García"
              value={form.name}
              onChange={set('name')}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
            <input
              className={fieldClass(!!emailError)}
              type="email"
              placeholder="correo@ejemplo.com"
              value={form.email}
              onChange={set('email')}
              onBlur={validateEmail}
            />
            {emailError && (
              <p className="flex items-center gap-1 mt-1 text-xs text-red-500">
                <AlertCircle className="h-3 w-3" />
                {emailError}
              </p>
            )}
          </div>

          {/* Teléfono + País */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Teléfono</label>
              <input
                className={fieldClass()}
                type="tel"
                placeholder="+54 9 11 ..."
                value={form.phone}
                onChange={set('phone')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">País / Ciudad</label>
              <input
                className={fieldClass()}
                placeholder="Argentina"
                value={form.country}
                onChange={set('country')}
              />
            </div>
          </div>

          {/* Empresa + Rol */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Empresa</label>
              <input
                className={fieldClass()}
                placeholder="Empresa S.A."
                value={form.company}
                onChange={set('company')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Rol</label>
              <input
                className={fieldClass()}
                placeholder="CEO, Vendedor..."
                value={form.role}
                onChange={set('role')}
              />
            </div>
          </div>

          {/* Canal de origen */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Canal de origen</label>
            <select
              className={`${fieldClass()} appearance-none cursor-pointer`}
              value={form.source}
              onChange={set('source')}
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="telegram">Telegram</option>
              <option value="email">Email</option>
              <option value="webchat">Chat en sitio web</option>
              <option value="manual">Ingreso manual</option>
            </select>
          </div>

          {/* Etiquetas */}
          {tags.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                <span className="flex items-center gap-1.5">
                  <Tag className="h-3.5 w-3.5" />
                  Etiquetas
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const selected = selectedTagIds.includes(tag.id)
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className={`flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                        selected ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                      style={selected ? { backgroundColor: tag.color, borderColor: tag.color } : undefined}
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: selected ? 'white' : tag.color }}
                      />
                      {tag.name}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Notas */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Notas</label>
            <textarea
              className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-1 focus:border-[#075f55] focus:ring-[#075f55]/20 resize-none transition-colors"
              rows={3}
              placeholder="Añade notas sobre este contacto..."
              value={form.notes}
              onChange={set('notes')}
            />
          </div>

          {/* Info box */}
          <div className="flex items-start gap-3 bg-[#075f55]/5 border border-[#075f55]/10 rounded-xl p-4">
            <Zap className="h-4 w-4 text-[#075f55] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-600">
              <span className="font-semibold text-[#075f55]">Sincronización automática.</span>{' '}
              El contacto estará disponible de inmediato en tus conversaciones y flujos de IA.
            </p>
          </div>

          {createMutation.isError && (
            <p className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
              {(createMutation.error as any)?.response?.data?.error ?? 'Error al crear el contacto. Intenta nuevamente.'}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
          <button
            onClick={handleSubmit}
            disabled={!form.waContact.trim() || createMutation.isPending || !!emailError || !!phoneError}
            className="w-full h-14 bg-[#075f55] hover:bg-[#064e46] disabled:opacity-50 text-white rounded-xl font-bold text-sm transition-colors"
          >
            {createMutation.isPending ? 'Guardando...' : 'Guardar contacto'}
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}

// ────────── Main Page ──────────
export default function ContactsPage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('Todos')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedTag, setSelectedTag] = useState('')
  const [selectedChannel, setSelectedChannel] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [showNewModal, setShowNewModal] = useState(false)
  const [page, setPage] = useState(1)
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)

  // 300ms search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.getAll(),
  })

  const handleTabChange = (newTab: Tab) => {
    setTab(newTab)
    setPage(1)
  }

  const filters: ContactsFilters = {
    search: debouncedSearch.trim() || undefined,
    tag: selectedTag || undefined,
    channel: selectedChannel || undefined,
    status: selectedStatus || undefined,
    isFavorite: tab === 'Favoritos' ? true : undefined,
    recent: tab === 'Recientes' ? true : undefined,
    page,
    limit: 30,
  }

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', filters],
    queryFn: () => contactsApi.getContacts(filters),
    keepPreviousData: true,
  } as any)

  const contacts: Contact[] = (data as any)?.data ?? []
  const pagination = (data as any)?.pagination

  const clearFilters = () => {
    setSelectedTag('')
    setSelectedChannel('')
    setSelectedStatus('')
    setSearchTerm('')
    setDebouncedSearch('')
    setPage(1)
  }

  const hasFilters = !!searchTerm || !!selectedTag || !!selectedChannel || !!selectedStatus || tab !== 'Todos'

  // Server-side filtering now provides complete accurate paginated data
  const visibleContacts = contacts

  const selectedTagLabel = tags.find((t) => t.id === selectedTag)?.name
  const selectedChannelLabel = CHANNELS.find((c) => c.value === selectedChannel)?.label
  const selectedStatusLabel = STATUSES.find((s) => s.value === selectedStatus)?.label

  return (
    <div
      className="flex flex-col h-full -mx-3 sm:-mx-4 lg:-mx-6 xl:-mx-8 -mt-4 sm:-mt-6 bg-white rounded-xl border border-gray-200 shadow-soft overflow-hidden"
      style={{ height: 'calc(100svh - 64px - 1rem)' }}
    >
      {/* Header */}
      <div className="bg-white px-4 pt-4 pb-3 space-y-3 border-b border-gray-100 flex-shrink-0">
        {/* Title row */}
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-[#075f55]" />
          <h1 data-tour="contacts-main" className="text-lg font-bold text-gray-900">Contactos</h1>
          {pagination && (
            <span className="text-xs text-gray-400 font-medium">{pagination.total}</span>
          )}

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() =>
                downloadContactsCsv(
                  visibleContacts,
                  `contactos-${tab.toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`
                )
              }
              disabled={visibleContacts.length === 0}
              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-40 font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
              title="Exportar contactos a CSV"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Exportar</span>
            </button>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1.5"
              >
                <X className="h-3 w-3" />
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="flex w-full items-stretch rounded-xl h-12 bg-slate-100">
          <div className="flex items-center justify-center pl-4 text-slate-500">
            <Search className="h-5 w-5" />
          </div>
          <input
            className="flex w-full border-none bg-transparent focus:ring-0 focus:outline-none text-slate-900 placeholder:text-slate-500 text-sm px-3"
            placeholder="Buscar contactos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Buscar contactos"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('')
                setDebouncedSearch('')
                setPage(1)
              }}
              className="flex items-center justify-center pr-3 text-slate-400 hover:text-slate-600 focus:outline-none"
              aria-label="Borrar búsqueda"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-0.5 no-scrollbar">

          {/* Etiquetas */}
          <FilterChip
            icon={<span className="w-2 h-2 rounded-full bg-current opacity-70" />}
            label={selectedTagLabel ?? 'Etiquetas'}
            active={!!selectedTag}
          >
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-500 hover:bg-gray-50"
              onClick={() => { setSelectedTag(''); setPage(1) }}
            >
              Todas las etiquetas
            </button>
            {tags.map((tag) => (
              <button
                key={tag.id}
                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 ${selectedTag === tag.id ? 'font-semibold' : ''}`}
                onClick={() => { setSelectedTag(tag.id); setPage(1) }}
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                {tag.name}
              </button>
            ))}
          </FilterChip>

          {/* Canal */}
          <FilterChip
            icon={<span className="text-[11px] font-bold opacity-70">●</span>}
            label={selectedChannelLabel && selectedChannel ? selectedChannelLabel : 'Canal'}
            active={!!selectedChannel}
          >
            {CHANNELS.map((ch) => (
              <button
                key={ch.value}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${selectedChannel === ch.value ? 'font-semibold text-[#075f55]' : 'text-gray-700'}`}
                onClick={() => { setSelectedChannel(ch.value); setPage(1) }}
              >
                {ch.label}
              </button>
            ))}
          </FilterChip>

          {/* Estado */}
          <FilterChip
            icon={<span className="text-[11px] font-bold opacity-70">◆</span>}
            label={selectedStatusLabel && selectedStatus ? selectedStatusLabel : 'Estado'}
            active={!!selectedStatus}
          >
            {STATUSES.map((st) => (
              <button
                key={st.value}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${selectedStatus === st.value ? 'font-semibold text-[#075f55]' : 'text-gray-700'}`}
                onClick={() => { setSelectedStatus(st.value); setPage(1) }}
              >
                {st.label}
              </button>
            ))}
          </FilterChip>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-white px-4 gap-8 flex-shrink-0">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => handleTabChange(t)}
            className={`flex flex-col items-center justify-center border-b-[3px] pb-3 pt-2 transition-colors text-sm tracking-tight ${
              tab === t
                ? 'border-[#075f55] text-[#075f55] font-bold'
                : 'border-transparent text-slate-500 hover:text-slate-700 font-medium'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Contact list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <div className="skeleton w-12 h-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton-text w-36" />
                  <div className="skeleton-text-sm w-48" />
                </div>
              </div>
            ))}
          </div>
        ) : visibleContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-6">
            <div className="w-16 h-16 mb-4 bg-slate-100 rounded-full flex items-center justify-center text-slate-400">
              {tab === 'Favoritos' ? (
                <Star className="h-8 w-8 text-amber-400 fill-amber-400/20" />
              ) : tab === 'Recientes' ? (
                <Clock className="h-8 w-8 text-[#075f55]" />
              ) : debouncedSearch ? (
                <Search className="h-8 w-8" />
              ) : (
                <Users className="h-8 w-8" />
              )}
            </div>
            <h4 className="text-base font-semibold text-gray-800">
              {tab === 'Favoritos'
                ? 'No tienes contactos favoritos'
                : tab === 'Recientes'
                ? 'Sin actividad reciente'
                : debouncedSearch
                ? `No encontramos resultados para "${debouncedSearch}"`
                : 'No se encontraron contactos'}
            </h4>
            <p className="text-sm text-gray-400 mt-1 max-w-sm">
              {tab === 'Favoritos'
                ? 'Marca contactos con la estrella en su perfil para verlos rápidamente aquí.'
                : tab === 'Recientes'
                ? 'Los contactos con interacción en las últimas 24 horas aparecerán en esta pestaña.'
                : hasFilters
                ? 'Prueba a cambiar o limpiar los filtros seleccionados.'
                : 'Crea tu primer contacto usando el botón inferior.'}
            </p>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="mt-3 px-3 py-1.5 text-xs font-semibold text-[#075f55] bg-[#075f55]/10 hover:bg-[#075f55]/20 rounded-lg transition-colors"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {visibleContacts.map((contact) => {
              const active = isRecentlyActive(contact.lastContactAt)
              return (
                <div
                  key={contact.id}
                  onClick={() => setSelectedContactId(contact.waContact)} // F3: Open the right drawer using the normalized waContact since ContactPanel uses waContact as ID, wait no, ContactPanel receives contactId from Chat but the query uses contactId string, which supports UUID or waContact. Either is fine.
                  className="flex items-center gap-4 p-3 hover:bg-slate-50 transition-colors cursor-pointer group rounded-xl"
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${avatarColor(contact.id)}`}>
                      {initials(contact)}
                    </div>
                    {active && (
                      <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-[#075f55] border-2 border-white" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 truncate">
                        {contact.name || contact.waContact}
                      </h3>
                      {contact.isFavorite && (
                        <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                      )}
                      {contact.status === 'blocked' && (
                        <span className="px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 text-[10px] font-bold uppercase flex items-center gap-1">
                          <Ban className="w-3 h-3" /> Bloqueado
                        </span>
                      )}
                      {contact.status === 'inactive' && (
                        <span className="px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-bold uppercase">
                          Inactivo
                        </span>
                      )}
                      {active && <span className="w-2 h-2 rounded-full bg-[#075f55] flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-2">
                      <span className="truncate">Última interacción: {formatLastContact(contact.lastContactAt)}</span>
                      {contact.source && (
                        <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] uppercase font-medium">
                          {contact.source}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                </div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary btn-sm disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="text-xs text-gray-500">
              {page} / {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="btn-secondary btn-sm disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => setShowNewModal(true)}
        className="fixed bottom-24 right-6 flex items-center gap-2 bg-[#075f55] hover:bg-[#064e46] text-white px-5 py-3 rounded-full shadow-lg transition-all z-30 text-sm font-semibold"
      >
        <UserPlus className="h-4 w-4" />
        Nuevo contacto
      </button>

      {/* Create modal */}
      {showNewModal && (
        <NewContactModal
          onClose={() => setShowNewModal(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['contacts'] })}
        />
      )}

      {/* Details Side Panel */}
      <ContactPanel 
        contactId={selectedContactId || ''} 
        isOpen={!!selectedContactId} 
        onClose={() => setSelectedContactId(null)} 
      />
    </div>
  )
}
